import {
  collection,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  updateDoc,
  doc,
  getDocs,
  serverTimestamp,
  writeBatch
} from 'firebase/firestore';
import { db, auth, functions } from './firebase';
import { onAuthStateChanged } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { trackFirebaseError } from './utils/errorTracking';

// Cloud Function for creating notifications
const createNotificationsFn = httpsCallable(functions, 'createNotifications');

/**
 * Create notifications for a comment via Cloud Function
 * All notification logic (including watcher lookup) is handled server-side for privacy
 * @param {Object} comment - The comment that was created
 * @param {Object|null} parentComment - The parent comment if this is a reply
 * @param {Array<string>} mentionedAddresses - Addresses mentioned in the comment
 */
export const createNotifications = async (comment, parentComment = null, mentionedAddresses = []) => {
  try {
    // Determine if this is a coin comment
    const isCoinComment = comment.targetType === 'coin';

    // Send minimal payload to Cloud Function
    await createNotificationsFn({
      commentId: comment.id,
      authorAddress: comment.authorAddress,
      // Include targetType for coin vs address distinction
      targetType: comment.targetType || 'address',
      // Address-specific fields
      targetAddress: comment.targetAddress,
      chain: comment.chain || comment.authorChain,
      // Coin-specific fields
      ...(isCoinComment && {
        targetCoinId: comment.targetCoinId,
        targetCoinName: comment.targetCoinName
      }),
      level: comment.level,
      text: (comment.text || '').substring(0, 100),
      parentAuthorAddress: parentComment?.authorAddress,
      parentCommentId: parentComment?.id,
      mentionedAddresses
    });
  } catch (error) {
    console.error('Error creating notifications:', error);
    trackFirebaseError(error, 'createNotifications', {
      commentId: comment?.id,
      targetType: comment?.targetType,
      targetAddress: comment?.targetAddress,
      targetCoinId: comment?.targetCoinId
    });
    // Don't throw - notifications are non-critical
  }
};

/**
 * Subscribe to user's notifications with real-time updates
 * Note: Watched addresses are now handled by creating duplicate notifications,
 * so we only need to query the user's own subcollection
 * @param {string} userAddress - DEPRECATED: Not used, kept for compatibility
 * @param {Array<string>} watchedAddresses - DEPRECATED: Not used, kept for compatibility
 * @param {Function} callback - Called with notifications array on updates
 * @returns {Function} Unsubscribe function
 */
export const subscribeToNotifications = (userAddress, watchedAddresses = [], callback) => {
  // Helper to set up the actual subscription
  const setupSubscription = (compositeId) => {
    const notificationsQuery = query(
      collection(db, 'addresses', compositeId, 'notifications'),
      where('deleted', '==', false),  // Exclude deleted notifications
      orderBy('createdAt', 'desc'),
      limit(50)
    );

    return onSnapshot(
      notificationsQuery,
      (snapshot) => {
        const notifications = snapshot.docs.map(doc => {
          const data = doc.data();
          return {
            id: doc.id,
            ...data,
            // Convert Firestore timestamp to JS date for easier handling
            createdAt: data.createdAt?.toDate() || new Date(),
            readAt: data.readAt?.toDate() || null
          };
        });

        callback(notifications);
      },
      (error) => {
        console.error('Error subscribing to notifications:', error);

        // Track to Sentry (especially important for index errors)
        trackFirebaseError(error, 'subscribeToNotifications', {
          compositeId,
          errorCode: error.code,
          errorMessage: error.message
        });

        callback([]);
      }
    );
  };

  // Check authentication - if not ready, wait for auth state
  if (!auth.currentUser) {
    let unsubscribeAuth = null;
    let unsubscribeSnapshot = null;
    let settled = false;

    unsubscribeAuth = onAuthStateChanged(auth, (user) => {
      if (settled) return;

      if (user) {
        settled = true;
        unsubscribeSnapshot = setupSubscription(user.uid);
        if (unsubscribeAuth) {
          unsubscribeAuth();
          unsubscribeAuth = null;
        }
      } else {
        // User is definitely not logged in
        settled = true;
        callback([]);
        if (unsubscribeAuth) {
          unsubscribeAuth();
          unsubscribeAuth = null;
        }
      }
    });

    return () => {
      if (unsubscribeAuth) unsubscribeAuth();
      if (unsubscribeSnapshot) unsubscribeSnapshot();
    };
  }

  return setupSubscription(auth.currentUser.uid);
};

/**
 * Mark a single notification as read for a specific user
 * @param {string} notificationId - The notification ID to mark as read
 * @param {string} userAddress - DEPRECATED: Not used, kept for compatibility
 */
export const markNotificationAsRead = async (notificationId, userAddress) => {
  // Check authentication
  if (!auth.currentUser) {
    throw new Error('You must be signed in to mark notifications as read');
  }

  try {
    const compositeId = auth.currentUser.uid;
    const notificationRef = doc(db, 'addresses', compositeId, 'notifications', notificationId);
    await updateDoc(notificationRef, {
      read: true,
      readAt: serverTimestamp()
    });
  } catch (error) {
    console.error('Error marking notification as read:', error);
    trackFirebaseError(error, 'markNotificationAsRead', {
      notificationId,
      compositeId: auth.currentUser.uid
    });
    throw error;
  }
};

/**
 * Mark all notifications as read for a user
 * @param {string} userAddress - DEPRECATED: Not used, kept for compatibility
 */
export const markAllNotificationsAsRead = async (userAddress) => {
  // Check authentication
  if (!auth.currentUser) {
    throw new Error('You must be signed in to mark notifications as read');
  }

  try {
    const compositeId = auth.currentUser.uid;
    const notificationsQuery = query(
      collection(db, 'addresses', compositeId, 'notifications'),
      where('read', '==', false),
      where('deleted', '==', false)
    );

    const snapshot = await getDocs(notificationsQuery);

    if (snapshot.empty) {
      return;
    }

    const batch = writeBatch(db);
    snapshot.docs.forEach(docSnapshot => {
      batch.update(docSnapshot.ref, {
        read: true,
        readAt: serverTimestamp()
      });
    });

    await batch.commit();
    // Notifications marked as read
  } catch (error) {
    console.error('Error marking all notifications as read:', error);
    trackFirebaseError(error, 'markAllNotificationsAsRead', {
      compositeId: auth.currentUser?.uid
    });
    throw error;
  }
};

/**
 * Get count of unread notifications for a user
 * @param {Array} notifications - Array of notification objects
 * @returns {number} Count of unread notifications
 */
export const getUnreadCount = (notifications) => {
  return notifications.filter(n => !n.read).length;
};

/**
 * Delete (hide) a notification for a specific user
 * @param {string} notificationId - The notification ID to delete
 * @param {string} userAddress - DEPRECATED: Not used, kept for compatibility
 */
export const deleteNotification = async (notificationId, userAddress) => {
  // Check authentication
  if (!auth.currentUser) {
    throw new Error('You must be signed in to delete notifications');
  }

  try {
    const compositeId = auth.currentUser.uid;
    const notificationRef = doc(db, 'addresses', compositeId, 'notifications', notificationId);
    await updateDoc(notificationRef, {
      deleted: true,
      deletedAt: serverTimestamp()
    });
    // Notification hidden for user
  } catch (error) {
    console.error('Error deleting notification:', error);
    trackFirebaseError(error, 'deleteNotification', {
      notificationId,
      compositeId: auth.currentUser.uid
    });
    throw error;
  }
};
